import { cn, Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react'
import { useMemo, useState } from 'react'
import { InitialsAvatar } from '~/components/common/InitialsAvatar'
import { useWorkspaceMembers } from '~/hooks/organization/useWorkspaceMembers'

interface UserPopoverProps {
  userId: number
  userName: string
  /** Show contact icon before user name in trigger */
  showContactIcon?: boolean
  /** Trigger container classes for quick per-callsite restyling */
  triggerClassName?: string
  /** User name text classes for quick per-callsite restyling */
  nameClassName?: string
  /** Contact icon classes */
  iconClassName?: string
}

export function UserPopover({
  userName,
  userId,
  showContactIcon = false,
  triggerClassName,
  nameClassName,
  iconClassName
}: UserPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { members } = useWorkspaceMembers()
  const safeMembers = Array.isArray(members) ? members : []

  const user = useMemo(() => {
    const memberInfo = safeMembers.find(m => m.id === userId)
    return memberInfo
      ? { name: memberInfo.name, email: memberInfo.email, role: memberInfo.role }
      : undefined
  }, [userId, safeMembers])

  const handleMouseEnter = () => setIsOpen(true)
  const handleMouseLeave = () => setIsOpen(false)

  if (!userName || !userId) {
    return null
  }

  return (
    <Popover placement='right' isOpen={isOpen} onOpenChange={open => setIsOpen(open)}>
      <PopoverTrigger>
        <button
          type='button'
          className={cn(
            'inline-flex items-center gap-1 cursor-pointer bg-transparent border-0 p-0 text-inherit',
            triggerClassName ?? 'text-link'
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {showContactIcon ? (
            <div className={cn('w-[16px] h-[16px]', iconClassName)}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='12'
                height='14'
                viewBox='0 0 12 14'
                fill='none'
              >
                <path
                  d='M6 1.33333C4.89543 1.33333 4 2.22876 4 3.33333C4 4.4379 4.89543 5.33333 6 5.33333C7.10457 5.33333 8 4.4379 8 3.33333C8 2.22876 7.10457 1.33333 6 1.33333ZM2.66667 3.33333C2.66667 1.49238 4.15905 0 6 0C7.84095 0 9.33333 1.49238 9.33333 3.33333C9.33333 5.17428 7.84095 6.66667 6 6.66667C4.15905 6.66667 2.66667 5.17428 2.66667 3.33333ZM2.33483 10.3471C1.65279 10.9229 1.33333 11.7223 1.33333 12.6667V13.3333H0V12.6667C0 11.4003 0.44245 10.1998 1.47469 9.32827C2.49245 8.469 4.00016 8 6 8C7.99984 8 9.50755 8.469 10.5253 9.32827C11.5576 10.1998 12 11.4003 12 12.6667V13.3333H10.6667V12.6667C10.6667 11.7223 10.3472 10.9229 9.66517 10.3471C8.96864 9.759 7.80969 9.33333 6 9.33333C4.19032 9.33333 3.03136 9.759 2.33483 10.3471Z'
                  fill='#5B5BD5'
                />
              </svg>
            </div>
          ) : null}
          <span className={nameClassName}>{userName}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='rounded-md shadow-2xl py-4 pl-4 pr-6 border-none ring-1 ring-[#E5E7EB]'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className='user-profile flex'>
          <div className='user-profile__avatar mr-4'>
            <InitialsAvatar name={user?.name ?? userName} size='md' />
          </div>
          <div className='user-profile__info'>
            <div className='user-profile__name'>{user?.name ?? userName}</div>
            {user?.email ? (
              <div className='user-profile__email text-gray-500'>{user?.email}</div>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
